import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { chatFetch, extractErrorMessage } from '../../Services/authClient';

const TeamsPage = () => {
  const { t } = useTranslation();
  const [teams, setTeams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    chatFetch('/chat-api/v2/categories/')
      .then((payload) => {
        const list = Array.isArray(payload) ? payload : payload?.results || [];
        setCategories(list);
      })
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    setStatus('loading');
    chatFetch('/chat-api/fanzones/')
      .then((payload) => {
        const list = Array.isArray(payload) ? payload : payload?.results || [];
        setTeams(list);
        setStatus(list.length === 0 ? 'empty' : 'ready');
      })
      .catch((error) => setStatus(extractErrorMessage(error, t('app.teams.loadError', 'Unable to load teams.'))));
  }, [t]);

  const filteredTeams = useMemo(() => {
    return teams.filter((team) => {
      const matchesCategory = selectedCategory === 'all' || team.category === selectedCategory || team.category_slug === selectedCategory;
      const haystack = `${team.name || ''} ${team.description || ''}`.toLowerCase();
      const matchesSearch = haystack.includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [teams, selectedCategory, searchTerm]);

  return (
    <div className="teams">
      <div className="teams__toolbar">
        <div className="teams__search">
          <input
            type="search"
            placeholder={t('app.teams.search', 'Search your teams…')}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        <div className="teams__categories">
          <button
            type="button"
            className={selectedCategory === 'all' ? 'is-active' : ''}
            onClick={() => setSelectedCategory('all')}
          >
            {t('app.teams.categoryAll', 'All')}
          </button>
          {categories.map((category) => (
            <button
              type="button"
              key={category.slug || category.id}
              className={selectedCategory === (category.slug || category.id) ? 'is-active' : ''}
              onClick={() => setSelectedCategory(category.slug || category.id)}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {status === 'loading' && <p className="teams__status">{t('app.teams.loading', 'Loading crews…')}</p>}
      {status !== 'loading' && filteredTeams.length === 0 && (
        <p className="teams__status">{t('app.teams.empty', 'No crews match that filter yet.')}</p>
      )}

      <div className="teams__grid">
        {filteredTeams.map((team) => (
          <article key={team.id} className="team-card">
            <div
              className="team-card__media"
              style={{
                backgroundImage: team.banner ? `url(${team.banner})` : 'linear-gradient(135deg, #0ea5e9, #9333ea)',
              }}
            />
            <div className="team-card__body">
              <div className="team-card__badge">{team.category || t('app.teams.defaultCategory', 'Sports')}</div>
              <h3>{team.name}</h3>
              <p>{team.description || t('app.teams.descriptionFallback', 'Go rally the squad!')}</p>
              <div className="team-card__meta">
                <span>{team.member_count || team.members_count || (team.members?.length ?? 0)} {t('app.teams.members', 'Members')}</span>
                {team.rival && <span>{t('app.teams.rival', 'Rival')}: {team.rival}</span>}
              </div>
              <div className="team-card__actions">
                <button type="button">{t('app.teams.makeFriend', 'Make Friend')}</button>
                <button type="button">{t('app.teams.shareThought', 'Share Thought')}</button>
                <button type="button">{t('app.teams.invite', 'Invite')}</button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default TeamsPage;
